/* =========================================================================
   ClueVine — stats logic (pure)
   The streak/distribution/history math lives here, free of React, so it can be
   unit-tested directly. StatsContext is a thin persistence + state wrapper over
   applyResult().

   Alongside per-mode tallies, a compact daily ledger records the first result
   for every (day, mode). It is what makes a daily "count once", powers the
   garden view / calendar / archive, and keeps full result cards for recent days
   so today's result can be reopened and shared.
   ========================================================================= */

import { dayNumber, migrateLegacyDay } from '../engine/seed';
import type { PlayMode, PuzzleStatus, ResultSummary, ScoreBreakdown } from '../engine/types';

export type FinalStatus = Exclude<PuzzleStatus, 'playing'>;

export interface ModeStats {
  played: number;
  won: number;
  currentStreak: number;
  maxStreak: number;
  /** Day number of the most recent daily that affected the streak. */
  lastDailyDay: number | null;
  /** Consecutive losses (any mode of play) — drives adaptive easing. */
  recentLossStreak: number;
  /** attempts → count of hint-free wins, for the Cipher guess-distribution chart. */
  guessDistribution: Record<number, number>;
  bestScore: number;
  totalScore: number;
  /** Most-recent answers (lowercased) for avoid-recent generation. */
  recentAnswers: string[];
}

export interface HistoryEntry {
  id: string;
  modeId: string;
  playMode: PlayMode;
  completedAt: number;
  status: FinalStatus;
  score: number;
  attempts?: number;
  answer?: string;
  difficultyId: string;
  seedLabel: string;
  shareGrid?: string[];
  dayNumber?: number;
  archive?: boolean;
  hintsUsed?: number;
}

/** Everything needed to show a finished daily's result card again. */
export interface DailyResult {
  breakdown: ScoreBreakdown;
  summary: ResultSummary;
  difficultyId: string;
  seedLabel: string;
}

export interface DailyRecord {
  status: FinalStatus;
  score: number;
  completedAt: number;
  /** Played after its day had passed — never touches streaks. */
  archive?: boolean;
  /** Full result card; kept only for recent days to bound storage. */
  result?: DailyResult;
}

/** day number (as a string key) → modeId → the first finished result that day. */
export type DailyLedger = Record<string, Record<string, DailyRecord>>;

export interface StatsState {
  modes: Record<string, ModeStats>;
  history: HistoryEntry[];
  dailies: DailyLedger;
}

export interface ResultRecord {
  modeId: string;
  playMode: PlayMode;
  status: FinalStatus;
  score: number;
  attempts?: number;
  answer?: string;
  /** Keys to remember for avoid-recent generation (defaults to [answer]). */
  avoidKeys?: string[];
  difficultyId: string;
  dayNumber?: number;
  /** A past day's daily played from the archive. */
  archive?: boolean;
  hintsUsed?: number;
  seedLabel: string;
  shareGrid?: string[];
  completedAt: number;
  /** Result card to keep in the daily ledger. */
  result?: DailyResult;
  /** Caller-supplied id keeps applyResult pure/deterministic. */
  historyId: string;
}

export const RECENT_ANSWERS_CAP = 30;
export const HISTORY_CAP = 100;
/** Days of full result cards kept in the ledger (older days keep status + score only). */
export const RESULT_KEEP_DAYS = 7;

export function emptyModeStats(): ModeStats {
  return {
    played: 0,
    won: 0,
    currentStreak: 0,
    maxStreak: 0,
    lastDailyDay: null,
    recentLossStreak: 0,
    guessDistribution: {},
    bestScore: 0,
    totalScore: 0,
    recentAnswers: [],
  };
}

export function emptyStats(): StatsState {
  return { modes: {}, history: [], dailies: {} };
}

export function getModeStats(state: StatsState, modeId: string): ModeStats {
  return state.modes[modeId] ?? emptyModeStats();
}

export function dailyRecord(state: StatsState, day: number, modeId: string): DailyRecord | undefined {
  return state.dailies[String(day)]?.[modeId];
}

function compactLedger(ledger: DailyLedger, newestDay: number): DailyLedger {
  const cutoff = newestDay - RESULT_KEEP_DAYS;
  let out = ledger;
  for (const key of Object.keys(ledger)) {
    if (Number(key) >= cutoff) continue;
    const day = ledger[key];
    if (!Object.values(day).some((r) => r.result)) continue;
    if (out === ledger) out = { ...ledger };
    const slim: Record<string, DailyRecord> = {};
    for (const [modeId, rec] of Object.entries(day)) {
      const { result: _drop, ...rest } = rec;
      slim[modeId] = rest;
    }
    out[key] = slim;
  }
  return out;
}

export function applyResult(state: StatsState, r: ResultRecord): StatsState {
  const isDaily = r.playMode === 'daily' && typeof r.dayNumber === 'number';

  // A daily counts once: a second finish for the same day+mode changes nothing.
  if (isDaily && dailyRecord(state, r.dayNumber!, r.modeId)) return state;

  const prev = getModeStats(state, r.modeId);
  const next: ModeStats = {
    ...prev,
    guessDistribution: { ...prev.guessDistribution },
    recentAnswers: [...prev.recentAnswers],
  };

  next.played += 1;
  if (r.status === 'won') {
    next.won += 1;
    next.recentLossStreak = 0;
  } else {
    next.recentLossStreak += 1;
  }

  next.totalScore += r.score;
  next.bestScore = Math.max(next.bestScore, r.score);

  // Hinted wins don't enter the distribution (a fully-revealed word isn't a "1").
  if (r.status === 'won' && typeof r.attempts === 'number' && !r.hintsUsed) {
    next.guessDistribution[r.attempts] = (next.guessDistribution[r.attempts] ?? 0) + 1;
  }

  const keys = (r.avoidKeys ?? (r.answer ? [r.answer] : [])).map((k) => k.toLowerCase()).filter(Boolean);
  if (keys.length) {
    next.recentAnswers = [...keys, ...next.recentAnswers.filter((x) => !keys.includes(x))].slice(
      0,
      RECENT_ANSWERS_CAP,
    );
  }

  let dailies = state.dailies;
  if (isDaily) {
    const day = r.dayNumber!;

    // Daily streak — endless and archive play never affect it.
    if (!r.archive) {
      const last = prev.lastDailyDay;
      if (r.status === 'won') {
        if (last === null || day > last + 1) {
          next.currentStreak = 1; // first daily, or a missed day restarts the streak
          next.lastDailyDay = day;
        } else if (day === last + 1) {
          next.currentStreak = prev.currentStreak + 1;
          next.lastDailyDay = day;
        }
        // day <= last (an older daily finished late): leave the streak untouched.
        next.maxStreak = Math.max(next.maxStreak, next.currentStreak);
      } else if (last === null || day > last) {
        next.currentStreak = 0;
        next.lastDailyDay = day;
      }
    }

    const key = String(day);
    const record: DailyRecord = {
      status: r.status,
      score: r.score,
      completedAt: r.completedAt,
      ...(r.archive ? { archive: true } : {}),
      ...(r.result ? { result: r.result } : {}),
    };
    dailies = { ...state.dailies, [key]: { ...(state.dailies[key] ?? {}), [r.modeId]: record } };
    const newest = Math.max(day, ...Object.keys(dailies).map(Number));
    dailies = compactLedger(dailies, newest);
  }

  const entry: HistoryEntry = {
    id: r.historyId,
    modeId: r.modeId,
    playMode: r.playMode,
    completedAt: r.completedAt,
    status: r.status,
    score: r.score,
    attempts: r.attempts,
    answer: r.answer,
    difficultyId: r.difficultyId,
    seedLabel: r.seedLabel,
    shareGrid: r.shareGrid,
    ...(isDaily ? { dayNumber: r.dayNumber } : {}),
    ...(r.archive ? { archive: true } : {}),
    ...(r.hintsUsed ? { hintsUsed: r.hintsUsed } : {}),
  };

  return {
    modes: { ...state.modes, [r.modeId]: next },
    history: [entry, ...state.history].slice(0, HISTORY_CAP),
    dailies,
  };
}

export function winRate(s: ModeStats): number {
  return s.played ? s.won / s.played : 0;
}

/**
 * The streak as it stands today: a streak whose last daily was before yesterday
 * has already lapsed, even though it's only zeroed when the next daily is played.
 */
export function liveStreak(s: ModeStats, today: number = dayNumber()): number {
  return s.lastDailyDay != null && s.lastDailyDay >= today - 1 ? s.currentStreak : 0;
}

/** A day "counts" for the garden when any (non-archive) daily was won on it. */
function gardenDayCounts(ledger: DailyLedger, day: number): boolean {
  const recs = ledger[String(day)];
  return recs ? Object.values(recs).some((r) => r.status === 'won' && !r.archive) : false;
}

export interface GardenStreak {
  /** Consecutive days (through today, or yesterday if today isn't done yet) with a daily win. */
  current: number;
  doneToday: boolean;
  /** The streak survives today only if a daily is won before midnight. */
  atRisk: boolean;
  best: number;
}

/** The one streak the whole garden shares: any mode's daily keeps it growing. */
export function gardenStreak(ledger: DailyLedger, today: number = dayNumber()): GardenStreak {
  const doneToday = gardenDayCounts(ledger, today);
  let current = 0;
  for (let d = doneToday ? today : today - 1; d >= 1 && gardenDayCounts(ledger, d); d--) current += 1;

  let best = 0;
  let run = 0;
  let prevDay = -Infinity;
  const days = Object.keys(ledger)
    .map(Number)
    .filter((d) => Number.isFinite(d) && gardenDayCounts(ledger, d))
    .sort((a, b) => a - b);
  for (const d of days) {
    run = d === prevDay + 1 ? run + 1 : 1;
    best = Math.max(best, run);
    prevDay = d;
  }
  return { current, doneToday, atRisk: !doneToday && current > 0, best: Math.max(best, current) };
}

export interface OverallStats {
  played: number;
  won: number;
  bestStreak: number;
  /** The streak to show on the vine: the garden streak (or a longer live per-mode one). */
  currentBestStreak: number;
  totalScore: number;
  garden: GardenStreak;
}

export function overall(state: StatsState, today: number = dayNumber()): OverallStats {
  let played = 0;
  let won = 0;
  let bestStreak = 0;
  let currentBestStreak = 0;
  let totalScore = 0;
  for (const s of Object.values(state.modes)) {
    played += s.played;
    won += s.won;
    bestStreak = Math.max(bestStreak, s.maxStreak);
    currentBestStreak = Math.max(currentBestStreak, liveStreak(s, today));
    totalScore += s.totalScore;
  }
  const garden = gardenStreak(state.dailies, today);
  return {
    played,
    won,
    bestStreak: Math.max(bestStreak, garden.best),
    currentBestStreak: Math.max(currentBestStreak, garden.current),
    totalScore,
    garden,
  };
}

/* ---- Validation & migration --------------------------------------------- */

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

function normalizeModeStats(raw: unknown): ModeStats {
  const base = emptyModeStats();
  if (!isObj(raw)) return base;
  const dist: Record<number, number> = {};
  if (isObj(raw.guessDistribution)) {
    for (const [k, v] of Object.entries(raw.guessDistribution)) {
      const n = Number(k);
      if (Number.isInteger(n) && n >= 0 && typeof v === 'number' && v > 0) dist[n] = Math.floor(v);
    }
  }
  return {
    played: Math.max(0, num(raw.played)),
    won: Math.max(0, num(raw.won)),
    currentStreak: Math.max(0, num(raw.currentStreak)),
    maxStreak: Math.max(0, num(raw.maxStreak)),
    lastDailyDay: typeof raw.lastDailyDay === 'number' && Number.isFinite(raw.lastDailyDay) ? raw.lastDailyDay : null,
    recentLossStreak: Math.max(0, num(raw.recentLossStreak)),
    guessDistribution: dist,
    bestScore: Math.max(0, num(raw.bestScore)),
    totalScore: Math.max(0, num(raw.totalScore)),
    recentAnswers: strArr(raw.recentAnswers).slice(0, RECENT_ANSWERS_CAP),
  };
}

function normalizeHistoryEntry(raw: unknown): HistoryEntry | null {
  if (!isObj(raw)) return null;
  if (typeof raw.id !== 'string' || typeof raw.modeId !== 'string') return null;
  if (raw.status !== 'won' && raw.status !== 'lost') return null;
  return {
    id: raw.id,
    modeId: raw.modeId,
    playMode: raw.playMode === 'daily' ? 'daily' : 'endless',
    completedAt: num(raw.completedAt),
    status: raw.status,
    score: Math.max(0, num(raw.score)),
    attempts: typeof raw.attempts === 'number' ? raw.attempts : undefined,
    answer: typeof raw.answer === 'string' ? raw.answer : undefined,
    difficultyId: typeof raw.difficultyId === 'string' ? raw.difficultyId : 'standard',
    seedLabel: typeof raw.seedLabel === 'string' ? raw.seedLabel : '',
    shareGrid: Array.isArray(raw.shareGrid) ? strArr(raw.shareGrid) : undefined,
    ...(typeof raw.dayNumber === 'number' ? { dayNumber: raw.dayNumber } : {}),
    ...(raw.archive === true ? { archive: true } : {}),
    ...(typeof raw.hintsUsed === 'number' && raw.hintsUsed > 0 ? { hintsUsed: raw.hintsUsed } : {}),
  };
}

function normalizeResult(raw: unknown): DailyResult | undefined {
  if (!isObj(raw) || !isObj(raw.breakdown) || !isObj(raw.summary)) return undefined;
  const lines = Array.isArray(raw.breakdown.lines) ? raw.breakdown.lines.filter(isObj) : null;
  if (!lines || typeof raw.breakdown.total !== 'number') return undefined;
  const summary = raw.summary;
  if ((summary.status !== 'won' && summary.status !== 'lost') || typeof summary.headline !== 'string') return undefined;
  return {
    breakdown: raw.breakdown as unknown as ScoreBreakdown,
    summary: summary as unknown as ResultSummary,
    difficultyId: typeof raw.difficultyId === 'string' ? raw.difficultyId : 'standard',
    seedLabel: typeof raw.seedLabel === 'string' ? raw.seedLabel : '',
  };
}

function normalizeLedger(raw: unknown): DailyLedger {
  const out: DailyLedger = {};
  if (!isObj(raw)) return out;
  for (const [day, recs] of Object.entries(raw)) {
    if (!/^\d{1,6}$/.test(day) || !isObj(recs)) continue;
    const dayOut: Record<string, DailyRecord> = {};
    for (const [modeId, rec] of Object.entries(recs)) {
      if (!isObj(rec) || (rec.status !== 'won' && rec.status !== 'lost')) continue;
      const result = normalizeResult(rec.result);
      dayOut[modeId] = {
        status: rec.status,
        score: Math.max(0, num(rec.score)),
        completedAt: num(rec.completedAt),
        ...(rec.archive === true ? { archive: true } : {}),
        ...(result ? { result } : {}),
      };
    }
    if (Object.keys(dayOut).length) out[day] = dayOut;
  }
  return out;
}

/**
 * Coerce anything (stored data, an imported backup) into a well-formed
 * StatsState. Missing or malformed pieces fall back to empty values rather
 * than crashing render code later.
 */
export function normalizeStats(raw: unknown): StatsState {
  if (!isObj(raw)) return emptyStats();
  const modes: Record<string, ModeStats> = {};
  if (isObj(raw.modes)) {
    for (const [id, ms] of Object.entries(raw.modes)) modes[id] = normalizeModeStats(ms);
  }
  const history = Array.isArray(raw.history)
    ? raw.history.map(normalizeHistoryEntry).filter((h): h is HistoryEntry => h !== null).slice(0, HISTORY_CAP)
    : [];
  return { modes, history, dailies: normalizeLedger(raw.dailies) };
}

/** True when a value is at least shaped like stats (used to reject bogus imports). */
export function looksLikeStats(raw: unknown): boolean {
  return isObj(raw) && isObj(raw.modes) && Array.isArray(raw.history);
}

/**
 * v1 → v2: day numbers were computed with a DST-sensitive formula, and there was
 * no daily ledger. Re-number lastDailyDay and backfill the ledger from history.
 */
export function migrateStatsV1(raw: unknown): StatsState {
  const s = normalizeStats(raw);
  for (const ms of Object.values(s.modes)) {
    if (ms.lastDailyDay != null) ms.lastDailyDay = migrateLegacyDay(ms.lastDailyDay);
  }
  const dailies: DailyLedger = {};
  // History is newest-first; walk oldest-first so the first finish of a day wins.
  for (const h of [...s.history].reverse()) {
    if (h.playMode !== 'daily' || !h.completedAt) continue;
    const key = String(dayNumber(new Date(h.completedAt)));
    const day = (dailies[key] ??= {});
    if (!day[h.modeId]) day[h.modeId] = { status: h.status, score: h.score, completedAt: h.completedAt };
  }
  return { ...s, dailies };
}
